import React, { useState, useRef, useEffect } from "react";
import Modal from "components/global/modal";
import Button from "components/button/button";
import toast from "react-hot-toast";
import { FaShare, FaCheck, FaRegCopy } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

export default function ShareViewModal({ hide, link = "", ...props }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  // clear the "Copied" reset timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const markCopied = () => {
    setCopied(true);
    toast.success(t("linkCopied"));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2500);
  };

  const handleCopy = async () => {
    try {
      // navigator.clipboard only exists on HTTPS / localhost
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        // fallback for non-secure contexts
        inputRef.current?.select();
        if (!document.execCommand("copy")) throw new Error("copy failed");
      }
      markCopied();
    } catch (err) {
      console.log("error@copyShareLink", err);
      // leave the text selected so the user can press Ctrl/Cmd + C
      inputRef.current?.select();
      toast.error(t("copyFailed", "Could not copy. Press Ctrl/Cmd + C to copy the link."));
    }
  };

  const Footer = () => {
    return (
      <div className="relative grid gap-y-3 1sm:gap-y-0 1sm:flex 1sm:flex-row 1sm:items-center 1sm:justify-end 1sm:space-x-2">
        <Button
          buttonName={t("close", "Close")}
          buttonClass="bg-thBg py-2 px-6"
          buttonTextClass="text-black text-xs 2sm:text-base font-semibold"
          onClick={() => hide()}
        />
        <Button
          key="copy-share-link-btn"
          buttonName={copied ? t("copied", "Copied") : t("copyLink", "Copy link")}
          buttonClass="bg-primary py-2 px-6"
          buttonTextClass="text-white text-xs 2sm:text-base font-semibold flex items-center justify-center"
          onClick={handleCopy}
        />
      </div>
    );
  };

  return (
    <Modal
      footer={<Footer />}
      width="w-19/20 md:w-16/20 2md:w-12/20 3md:w-9/20 1xl:w-8/20"
      contentClassName="rounded-lg overflow-auto custom-scroll"
      wrapperClassName="overflow-visible"
      footerClass="border-t border-gray"
    >
      <div className="w-full">
        {/* content */}
        <div className="px-8.5 py-6">
          <div className="flex flex-row items-center">
            <FaShare className="text-base 2sm:text-3xl text-primary mr-3 md:mr-2" />
            <p className="text-base 2sm:text-3xl text-primary font-bold tracking-tighter">
              {t("shareView", "Share View")}
            </p>
          </div>

          <div className="w-full py-2">
            <p className="text-xs 2sm:text-md mb-3">
              {t("shareViewMessage", "Anyone with this link can see your milestones with the current filters applied.")}
            </p>

            <div className="flex items-stretch w-full border border-gray rounded-md overflow-hidden">
              {/*
                No `type="text"` on purpose: the global `input[type="text"]`
                rule (index.css) applies floating-label padding (pt-5 pb-1) and
                an inner border, which pushed this read-only link off-centre.
                Omitting the attribute keeps it a text input while exempting it
                from those styles, so its own symmetric py-3 centres the text.
              */}
              <input
                ref={inputRef}
                readOnly
                value={link}
                onFocus={(e) => e.target.select()}
                aria-label={t("shareLink", "Share link")}
                className="flex-1 min-w-0 px-3 py-3 text-xs 2sm:text-sm text-black bg-white outline-none truncate"
              />
              <button
                type="button"
                onClick={handleCopy}
                aria-label={t("copyLink", "Copy link")}
                className="flex items-center justify-center px-4 border-l border-gray bg-thBg text-primary"
              >
                {copied ? <FaCheck className="w-4 h-4" /> : <FaRegCopy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        {/* end content */}
      </div>
    </Modal>
  );
}